import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageTitle } from "@/components/DashUI";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, Copy, Trash2 } from "lucide-react";

const PLATFORMS = ["TikTok", "Instagram Reels", "YouTube Shorts", "YouTube", "Facebook", "LinkedIn", "Email", "Skool Post"];
const TOPICS = ["private-pay healthcare placement", "senior placement", "home-care referrals", "respite placement",
  "care navigation", "provider networking", "family education", "healthcare business development",
  "AI automation", "building a placement company", "provider perspective", "placement economics"];

export default function ContentFactory() {
  const [platform, setPlatform] = useState("TikTok");
  const [topic, setTopic] = useState(TOPICS[0]);
  const [count, setCount] = useState(2);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/content").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const gen = async () => {
    setLoading(true);
    try { await api.post("/admin/content/generate", { platform, topic, count: Number(count) }); toast.success("Content generated"); load(); }
    catch { toast.error("Generation failed"); }
    finally { setLoading(false); }
  };
  const del = async (id) => { await api.delete(`/admin/content/${id}`); load(); };

  return (
    <div>
      <PageTitle title="Content Factory" subtitle="Generate founder-agnostic, compliance-checked content" />
      <Card className="mb-6 p-6">
        <div className="grid gap-4 md:grid-cols-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Platform</Label>
            <Select value={platform} onValueChange={setPlatform}><SelectTrigger data-testid="content-platform" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Topic</Label>
            <Select value={topic} onValueChange={setTopic}><SelectTrigger data-testid="content-topic" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{TOPICS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Count</Label>
            <Input data-testid="content-count" className="mt-1" type="number" min={1} max={5} value={count} onChange={(e) => setCount(e.target.value)} />
          </div>
          <div className="flex items-end">
            <Button data-testid="content-generate-btn" onClick={gen} disabled={loading} className="w-full bg-clinical hover:bg-clinical/90">
              <Sparkles className="mr-2 h-4 w-4" /> {loading ? "Generating…" : "Generate"}
            </Button>
          </div>
        </div>
      </Card>

      <div className="space-y-4">
        {items.map((c) => (
          <Card key={c.id} className="p-6" data-testid={`content-item-${c.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <span className="font-heading font-semibold text-navy">{c.platform}</span>
                <span className="ml-2 text-sm text-slate-400">{c.topic}</span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(c.output); toast.success("Copied"); }}><Copy className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(c.id)}><Trash2 className="h-4 w-4 text-slate-400 hover:text-destructive" /></Button>
              </div>
            </div>
            <pre className="mt-3 max-h-80 overflow-y-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-4 font-sans text-sm text-slate-700">{c.output}</pre>
          </Card>
        ))}
        {items.length === 0 && <Card className="p-10 text-center text-slate-400">No content generated yet.</Card>}
      </div>
    </div>
  );
}
